import { motion } from 'framer-motion';
import React from 'react';
import { FiCalendar, FiLock, FiMail, FiShield } from 'react-icons/fi';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const PrivacyPolicy = () => {
  const lastUpdated = "December 15, 2024";

  const sections = [
    {
      title: "Information We Collect",
      icon: FiLock,
      content: [
        {
          subtitle: "Personal Information",
          text: "We collect personal information that you voluntarily provide to us when you express interest in obtaining information about us or our services, participate in activities on our website, or contact us. This includes your name, email address, phone number, company name, and any other information you choose to provide."
        },
        {
          subtitle: "Automatically Collected Information",
          text: "We automatically collect certain information when you visit our website, including your IP address, browser type, operating system, access times, and the pages you have viewed directly before and after accessing our website."
        },
        {
          subtitle: "Cookies and Tracking Technologies",
          text: "We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: FiShield,
      content: [
        {
          subtitle: "Service Provision",
          text: "We use your information to provide, operate, and maintain our services, process your transactions, and communicate with you about our services."
        },
        {
          subtitle: "Communication",
          text: "We may use your information to send you technical notices, updates, security alerts, and support and administrative messages."
        },
        {
          subtitle: "Marketing",
          text: "With your consent, we may use your information to send you marketing communications about our services that may be of interest to you."
        },
        {
          subtitle: "Analytics and Improvement",
          text: "We use your information to understand and analyze how you use our website and services, and to develop new products, services, features, and functionality."
        }
      ]
    },
    {
      title: "Information Sharing and Disclosure",
      icon: FiMail,
      content: [
        {
          subtitle: "Service Providers",
          text: "We may share your information with third-party service providers who perform services on our behalf, such as web hosting, data analysis, and email delivery services."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency)."
        },
        {
          subtitle: "Business Transfers",
          text: "We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business."
        }
      ]
    },
    {
      title: "Data Security",
      icon: FiShield,
      content: [
        {
          subtitle: "Security Measures",
          text: "We use administrative, technical, and physical security measures to help protect your personal information. While we strive to protect your personal information, we cannot guarantee its absolute security."
        },
        {
          subtitle: "Data Retention",
          text: "We will retain your personal information only for as long as necessary to fulfill the purposes for which we collected it, including for the purposes of satisfying any legal, accounting, or reporting requirements."
        }
      ]
    },
    {
      title: "Your Rights and Choices",
      icon: FiLock,
      content: [
        {
          subtitle: "Access and Update",
          text: "You may update, correct, or delete your personal information by contacting us. We may need to verify your identity before processing your request."
        },
        {
          subtitle: "Marketing Communications",
          text: "You can opt out of receiving promotional communications from us by following the instructions in those communications or by contacting us directly."
        },
        {
          subtitle: "Cookies",
          text: "Most web browsers are set to accept cookies by default. You can usually choose to set your browser to remove or reject browser cookies."
        }
      ]
    },
    {
      title: "International Data Transfers",
      icon: FiShield,
      content: [
        {
          subtitle: "Cross-Border Transfers",
          text: "Your information, including personal data, may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction."
        }
      ]
    },
    {
      title: "Children's Privacy",
      icon: FiLock,
      content: [
        {
          subtitle: "Age Restrictions",
          text: "Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and become aware that your child has provided us with personal information, please contact us."
        }
      ]
    }
  ];

  const contactInfo = {
    email: "privacy@innofusion.com",
    phone: "+1 (555) 123-4567",
    address: "123 Innovation Drive, Tech City, TC 12345"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Legal Information
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Privacy
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Policy
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Your privacy is important to us. This Privacy Policy explains how Innofusion Analytics collects, 
              uses, and protects your personal information when you use our services.
            </p>
            <div className="flex items-center justify-center space-x-2 text-gray-500">
              <FiCalendar size={16} />
              <span className="text-sm font-body">Last updated: {lastUpdated}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 font-display tracking-tight">
                    {section.title}
                  </h2>
                </div>
                
                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      {item.subtitle && (
                        <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading tracking-wide">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-gray-600 font-body leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">
                Contact Us About Privacy
              </h2>
              <p className="text-xl text-gray-600 font-body font-light tracking-wide leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiMail className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Email</h3>
                <p className="text-gray-600 font-body">{contactInfo.email}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiMail className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Phone</h3>
                <p className="text-gray-600 font-body">{contactInfo.phone}</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl border border-blue-100">
                <FiMail className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 font-heading tracking-wide">Address</h3>
                <p className="text-gray-600 font-body text-sm">{contactInfo.address}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Updates Notice */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Policy Updates
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 